/**
 * PULSE CART DISCOUNT (2026-08-25)
 *
 * Applying and clearing both go through POST /cart/update.js with a `discount`
 * field. That is not in the AJAX API docs and the usual advice is the
 * GET /discount/CODE redirect, but the field is real and measured working on
 * 2026-08-25: it applies the code and returns the resulting cart in ONE call,
 * with discount_codes[].applicable saying whether it stuck. An empty string
 * clears every code.
 *
 * The trap it hides: `discount` REPLACES the whole code set. Send a typo while
 * a good code is on the cart and the good code is gone, silently, with the
 * customer looking at an error message about the typo. So the previous code is
 * remembered and re-applied whenever a new one is rejected.
 *
 * Shopify does not distinguish a code that does not exist from a real code this
 * bag does not qualify for -- both come back applicable:false -- so there is one
 * error message, not two.
 *
 * On success the drawer re-renders from ?section_id=cart-drawer, the same
 * section swap Dawn's own quantity controls use, so the totals, the saved line
 * and the checkout button all come back from Liquid rather than being patched
 * by hand in JS.
 */
class PulseCartDiscount extends HTMLElement {
  connectedCallback() {
    // The applied code and its Remove live in the totals block, outside this
    // element, because that is where the four money lines read as one list.
    // So the scope for controls is the footer, not the element.
    const scope = this.closest('.sc__foot') || this;
    this.input = this.querySelector('[data-discount-input]');
    this.button = this.querySelector('[data-discount-apply]');
    this.remove = scope.querySelector('[data-discount-remove]');
    this.message = this.querySelector('[data-discount-message]');
    this.applied = this.dataset.applied || '';

    if (this.button) this.button.addEventListener('click', () => this.submit(this.input.value));
    if (this.remove) this.remove.addEventListener('click', () => this.submit('', true));
    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.submit(this.input.value);
      });
    }
  }

  get root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  say(text, isError) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.classList.toggle('is-error', Boolean(isError));
  }

  busy(state) {
    [this.button, this.remove].forEach((el) => {
      if (el) el.disabled = state;
    });
  }

  // Any applied discount that did not come from a typed code: automatic and app
  // discounts, at cart level or allocated to a line. A markdown is not a discount
  // application, so a clearance bag alone never counts.
  hasOtherDiscount(cart) {
    const other = (app) => Boolean(app) && app.type !== 'discount_code';
    if ((cart.cart_level_discount_applications || []).some(other)) return true;
    return (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some((a) => other(a.discount_application)),
    );
  }

  async update(code) {
    const response = await fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({discount: code}),
    });
    if (!response.ok) throw new Error(`cart/update returned ${response.status}`);
    return response.json();
  }

  async submit(raw, clearing) {
    const code = (raw || '').trim();
    if (!clearing && !code) {
      this.say('Enter a code first.', true);
      this.input.focus();
      return;
    }

    this.busy(true);
    this.say(clearing ? 'Removing…' : 'Checking…', false);

    try {
      const cart = await this.update(clearing ? '' : code);

      if (!clearing) {
        const entry = (cart.discount_codes || []).find(
          (d) => d.code && d.code.toLowerCase() === code.toLowerCase(),
        );
        if (!entry || !entry.applicable) {
          // Put back whatever was working before this attempt: the request that
          // just failed also wiped it. With nothing to put back, the empty string
          // clears the rejected code, which would otherwise stay on the cart as
          // applicable:false.
          await this.update(this.applied || '');
          // A bag that already carries a discount which is NOT a code (the
          // UpPromote partner discount lands as an automatic "10% OFF ORDER") makes
          // this a combination clash, not a bad code: Shopify keeps the better of
          // the two and refuses the other. Andre, 2026-09-24: say it cannot be
          // combined. Wording by ChatGPT, run 20260924-105339-cart-message-r0-8f61.
          this.say(
            this.hasOtherDiscount(cart)
              ? "This code doesn't combine with the discount on your bag."
              : 'That code is not valid for this bag.',
            true,
          );
          return;
        }
      }

      await this.refreshDrawer();
    } catch (error) {
      this.say('Something went wrong. Try again.', true);
    } finally {
      this.busy(false);
    }
  }

  async refreshDrawer() {
    const response = await fetch(`${this.root}?section_id=cart-drawer`);
    const fresh = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('#CartDrawer');
    const target = document.querySelector('#CartDrawer');
    if (!fresh || !target) return;
    target.innerHTML = fresh.innerHTML;
  }
}

customElements.define('pulse-cart-discount', PulseCartDiscount);
